/**
 * 椭圆弧锚点几何（草图平面 2D 纯数据）。
 *
 * 等距实体曲线对偏移距离尺寸（`curve-offset-distance`）的椭圆侧锚点需要
 * 在椭圆弧上解析标注方向点：本文件提供渲染层与创建层共用的纯几何换算，
 * 保证"尺寸线锚点跟随标注位置、且不超出椭圆弧扫过角域"这一口径全局一致。
 */

/** 椭圆弧锚点几何（草图平面 2D 纯数据）。 */
export interface SketchEllipseAnchorGeometry {
    /** 椭圆中心点。 */
    readonly center: readonly [number, number];
    /** 长轴正向基准点（决定长轴方向与半长轴）。 */
    readonly major: readonly [number, number];
    /** 短半轴长度。 */
    readonly semiMinor: number;
    /** 弧起始角（相对长轴正向，弧度；THREE.EllipseCurve 语义）。 */
    readonly startAngle: number;
    /** 弧终止角（相对长轴正向，弧度；THREE.EllipseCurve 语义）。 */
    readonly endAngle: number;
    /** 是否顺时针采样（THREE.EllipseCurve 语义）。 */
    readonly clockwise: boolean;
}

/** 把角度规约到 [0, 2π)。 */
function normalizeAngleToTwoPi(angle: number): number {
    const normalized = Math.atan2(Math.sin(angle), Math.cos(angle));

    return normalized < 0 ? normalized + Math.PI * 2 : normalized;
}

/**
 * 判断椭圆弧是否覆盖整周（完整椭圆）。
 *
 * @param geometry 椭圆弧锚点几何。
 * @returns 起止角差接近 2π 时返回 true。
 */
export function isFullEllipseAnchorGeometry(geometry: SketchEllipseAnchorGeometry): boolean {
    return Math.abs(Math.abs(geometry.endAngle - geometry.startAngle) - Math.PI * 2) < 1e-9;
}

/**
 * 解析椭圆弧扫过角域的中点角。
 *
 * 完整椭圆不存在有意义的"弧中角"，返回 0（长轴正向，与旧锚点口径一致）。
 *
 * @param geometry 椭圆弧锚点几何。
 * @returns 弧域中点角（相对长轴正向，弧度）。
 */
export function resolveEllipseArcMidAngle(geometry: SketchEllipseAnchorGeometry): number {
    if (isFullEllipseAnchorGeometry(geometry)) {
        return 0;
    }

    // 扫过角域按采样方向计算：逆时针 = [start, start+span]，顺时针 = [start, start−span]
    const span = normalizeAngleToTwoPi(geometry.clockwise
        ? geometry.startAngle - geometry.endAngle
        : geometry.endAngle - geometry.startAngle);

    return geometry.clockwise ? geometry.startAngle - span / 2 : geometry.startAngle + span / 2;
}

/**
 * 把角度夹取到椭圆弧实际扫过的角域内。
 *
 * 说明：标注被拖到弧没画到的方向时，锚点不能悬在弧外——按圆周距离夹取到
 * 较近的弧端角，拖动行为连续且锚点始终落在可见弧段上。
 *
 * @param geometry 椭圆弧锚点几何。
 * @param angle 待夹取角度（相对长轴正向，弧度）。
 * @returns 弧域内的角度；完整椭圆原样返回。
 */
export function clampAngleToEllipseArcDomain(
    geometry: SketchEllipseAnchorGeometry,
    angle: number,
): number {
    if (isFullEllipseAnchorGeometry(geometry)) {
        return angle;
    }

    // 按采样方向把"角度相对弧起点的偏移"规约到 [0, 2π)
    const normalized = normalizeAngleToTwoPi(geometry.clockwise
        ? geometry.startAngle - angle
        : angle - geometry.startAngle);
    const span = normalizeAngleToTwoPi(geometry.clockwise
        ? geometry.startAngle - geometry.endAngle
        : geometry.endAngle - geometry.startAngle);

    if (normalized <= span) {
        return angle;
    }

    // 弧外：比较到弧两端点的圆周距离，夹取到较近一侧
    const distanceToStart = Math.min(normalized, Math.PI * 2 - normalized);
    const distanceToEnd = Math.min(
        Math.abs(normalized - span),
        Math.PI * 2 - Math.abs(normalized - span),
    );

    return distanceToStart <= distanceToEnd ? geometry.startAngle : geometry.endAngle;
}

/**
 * 计算椭圆上指定局部角（相对长轴正向）的点。
 *
 * @param geometry 椭圆弧锚点几何。
 * @param angle 局部角（弧度）。
 * @returns 椭圆参数点；半轴退化时退回长轴正向基准点。
 */
export function resolveEllipseAnchorPointAtAngle(
    geometry: SketchEllipseAnchorGeometry,
    angle: number,
): readonly [number, number] {
    const majorX = geometry.major[0] - geometry.center[0];
    const majorY = geometry.major[1] - geometry.center[1];
    const semiMajor = Math.hypot(majorX, majorY);

    if (semiMajor < 1e-12 || geometry.semiMinor < 1e-12) {
        return [geometry.major[0], geometry.major[1]];
    }

    const rotation = Math.atan2(majorY, majorX);
    const cosAngle = Math.cos(angle);
    const sinAngle = Math.sin(angle);
    const cosRotation = Math.cos(rotation);
    const sinRotation = Math.sin(rotation);

    // 椭圆参数点（长轴局部角 → 草图局部坐标）：p = center + R(rotation)·(a·cosθ, b·sinθ)
    return [
        geometry.center[0] + cosRotation * semiMajor * cosAngle
            - sinRotation * geometry.semiMinor * sinAngle,
        geometry.center[1] + sinRotation * semiMajor * cosAngle
            + cosRotation * geometry.semiMinor * sinAngle,
    ];
}

/**
 * 计算椭圆弧上朝目标方向的锚点。
 *
 * 与圆/弧锚点同口径：目标（标注位置）拖到哪，锚点就跟到哪个方向；
 * 方向角先换算到长轴局部坐标系，再夹取到弧实际扫过的角域内，保证
 * 锚点始终落在画出来的弧段上。目标与中心重合（方向退化）时退回
 * 长轴正向基准点。
 *
 * @param geometry 椭圆弧锚点几何。
 * @param direction 目标点（草图局部坐标，通常为标注位置）。
 * @returns 椭圆弧上的锚点。
 */
export function resolveEllipseAnchorPointToward(
    geometry: SketchEllipseAnchorGeometry,
    direction: readonly [number, number],
): readonly [number, number] {
    const offsetX = direction[0] - geometry.center[0];
    const offsetY = direction[1] - geometry.center[1];

    if (Math.hypot(offsetX, offsetY) < 1e-12) {
        return [geometry.major[0], geometry.major[1]];
    }

    const majorX = geometry.major[0] - geometry.center[0];
    const majorY = geometry.major[1] - geometry.center[1];
    const rotation = Math.atan2(majorY, majorX);
    const localAngle = Math.atan2(offsetY, offsetX) - rotation;

    return resolveEllipseAnchorPointAtAngle(
        geometry,
        clampAngleToEllipseArcDomain(geometry, localAngle),
    );
}
